import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { store } from 'redux/store';
import {
  selectCPMFTrackingForecastLocationId,
  selectCPMFTrackingForecastMultiLocationIds,
  setLocationId,
  setMultiLocationIds,
} from 'redux/reducers/meteorologyFieldsTrackingForecastControlPanelReducer';
import Location from 'types/Locations/Location';
import useLocations from 'hooks/Meteorology/Fields/TrackingForecast/Register/useLocations';

export default function useLocation() {
  const { locations: baseLocations } = useLocations();
  const selectedLocationId: number | undefined = useSelector(selectCPMFTrackingForecastLocationId);
  const selectedMultiLocationIds: number[] = useSelector(
    selectCPMFTrackingForecastMultiLocationIds,
  );

  const locations = useMemo(() => baseLocations || [], [baseLocations]);

  const selectedLocation = useMemo(() => {
    if (!selectedLocationId || !locations.length) return undefined;
    return locations.find((loc) => loc.id === selectedLocationId);
  }, [selectedLocationId, locations]);

  const selectedMultiLocations = useMemo(() => {
    if (!selectedMultiLocationIds.length || !locations.length) return [];
    return selectedMultiLocationIds
      .map((id) => locations.find((loc) => loc.id === id))
      .filter((location): location is Location => location !== undefined);
  }, [selectedMultiLocationIds, locations]);

  const setLocation = useCallback((id: number) => {
    const location = locations.find((loc) => loc.id === id);
    if (location) store.dispatch(setLocationId(id));
  }, [locations]);

  const setLocations = useCallback((newLocations: Location[]) => {
    const locationIds = newLocations.map((location) => location.id);
    store.dispatch(setMultiLocationIds(locationIds));
  }, []);

  const toggleMultiLocation = useCallback((value: number) => {
    const location = locations.find((loc) => loc.id === value);

    if (!location) return;

    const locationIsSelected = selectedMultiLocations.some((loc) => loc.id === location.id);
    const newLocations = locationIsSelected
      ? selectedMultiLocations.filter((l) => l.id !== location.id)
      : selectedMultiLocations.concat(location);
    setLocations(newLocations);
  }, [locations, selectedMultiLocations, setLocations]);

  const isLocationSelected = useCallback((id: number) => (
    selectedMultiLocationIds.includes(id)
  ), [selectedMultiLocationIds]);

  return {
    locations,
    selectedLocation,
    selectedMultiLocations,
    setLocation,
    setLocations,
    toggleMultiLocation,
    isLocationSelected,
  };
}
